import { ReactNode, createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import assign from 'lodash/assign';
import { useLazyQuery } from '@apollo/client';
import { useWatch, useFormContext } from 'react-hook-form';
import { GqlUpsertTemplateInvalidContentFailureTypes as TemplateErrorType } from 'modules/templateEditor/common/types';
import { Campaign, LabelConfig } from 'modules/workflows';
import {
  WorkflowsRootState,
  getIsClonedNodeInvalid,
  getIsNodeEditorReadOnly,
} from 'modules/workflows/ducks';
import { useDisplayCloneErrors } from 'modules/workflows/hooks/useDisplayCloneErrors';
import noop from 'utils/noop';
import { useFeatureFlags } from '../BaseSendEditor.hooks';
import {
  BaseSendEditorProps,
  CampaignErrors,
  SendNodeResources,
  TemplateError,
  TemplateErrors,
} from '../BaseSendEditor.types';
import {
  consolidateCampaignData,
  getCampaignErrors,
  getLabelConfigsFromNames,
} from '../BaseSendEditor.utils';
import { FetchPlacementByIdQuery } from '../BaseSendEditor.graphql';

export interface CampaignContextState extends Partial<BaseSendEditorProps> {
  errors: CampaignErrors;
  campaign: Partial<Campaign>;
  updateCampaign: (campaign: Partial<Campaign>, updateIsDirty?: boolean) => void;
  isDirty: boolean;
  updateIsDirty: (isDirty: boolean) => void;
  campaignLabels: LabelConfig[];
  isReadOnly: boolean;
  setIsReadOnly: (isReadOnly: boolean) => void;
  isCloneWarningBannerShown: boolean;
  cloneErrors: Record<string, boolean>;
  cloneDisplayValues: Record<string, string | string[]>;
  templateErrors: TemplateErrors;
  setTemplateErrors: (errors: TemplateErrors, callback: (errors: TemplateErrors) => void) => void;
  // The name of the placement -- only used in embedded
  placementName?: string;
  // Setter for the placement name
  setPlacementName: (placementName: string) => void;
}

export const CampaignContext = createContext<CampaignContextState>({
  errors: {},
  campaign: {},
  updateCampaign: noop,
  isDirty: false,
  updateIsDirty: noop,
  campaignLabels: [],
  isReadOnly: false,
  setIsReadOnly: noop,
  isCloneWarningBannerShown: false,
  cloneErrors: {},
  cloneDisplayValues: {},
  templateErrors: [],
  setTemplateErrors: noop,
  placementName: undefined,
  setPlacementName: noop,
});

export const CampaignContextProvider = ({
  children,
  ...rest
}: BaseSendEditorProps & { children: ReactNode }) => {
  const { isSendEditorImprovementsEnabled } = useFeatureFlags();
  const { nodeData, clonedFromNodeData, resources = {}, nodeId } = rest;
  const isInvalidNode = useSelector((state: WorkflowsRootState) =>
    getIsClonedNodeInvalid(state, nodeId),
  );
  const campaignLabels = useMemo(
    () => resources?.campaignLabels || [],
    [resources?.campaignLabels],
  );
  const [campaign, setCampaign] = useState(
    consolidateCampaignData(nodeData, resources as SendNodeResources),
  );
  const [placementName, setPlacementName] = useState<string | undefined>(undefined);

  const [fetchPlacementById] = useLazyQuery(FetchPlacementByIdQuery, {
    variables: {
      id: campaign.placementId,
    },
    onCompleted: (resp) => {
      setPlacementName(resp?.placement?.payload?.fields?.placementName);
    },
    fetchPolicy: 'cache-and-network',
  });

  const [errors, setErrors] = useState(getCampaignErrors(campaign));
  const [templateErrors, setTemplateErrors] = useState<TemplateErrors>([]);
  const [isDirty, setIsDirty] = useState(false);

  const filterTemplateErrors = useCallback(
    (dataErrors: any, callback: any) => {
      const filteredErrors = isInvalidNode
        ? dataErrors.filter((err: TemplateError) => {
            return [
              TemplateErrorType.MISSING_SNIPPETS,
              TemplateErrorType.INVALID_SNIPPET_PARAMS,
            ].includes(err.__typename);
          })
        : dataErrors;

      setTemplateErrors(filteredErrors);
      if (callback) callback(filteredErrors);
    },
    [templateErrors, setTemplateErrors],
  );

  const updateCampaign = useCallback(
    (data: Partial<Campaign> = {}, updateIsDirty = true) => {
      const updatedCampaign = assign({}, campaign, data);
      setCampaign(updatedCampaign);
      setErrors(getCampaignErrors(updatedCampaign));
      setIsDirty(updateIsDirty);
    },
    [campaign],
  );

  useEffect(() => {
    if (campaign.placementId) {
      fetchPlacementById();
    }
  }, [campaign?.placementId]);

  const clonedCampaign = clonedFromNodeData?.resource?.campaign || {};
  const cloneMessaging = useDisplayCloneErrors({
    clonedFromNodeData: {
      conversions: !!clonedCampaign.conversions?.length,
      customConversion: clonedCampaign.customConversion?.eventName,
      contentErrors: isInvalidNode && templateErrors,
    },
    fieldsToValidate: {
      conversions: {
        isValid: campaign.conversions?.length,
        displayValue: clonedCampaign.conversions?.map((c) => c.events[0]?.name),
      },
      customConversion: {
        isValid: campaign.customConversion?.eventName,
      },
      contentErrors: {
        isValid: templateErrors.length === 0,
      },
    },
  });

  const isNodeEditorReadOnly = useSelector(getIsNodeEditorReadOnly);
  const [isReadOnly, setIsReadOnly] = useState(isNodeEditorReadOnly);

  const values = useWatch();
  const {
    formState: { isDirty: isCampaignFormDirty },
  } = useFormContext();
  const campaignValues = useMemo(() => {
    return {
      ...campaign,
      ...values,
      labels: getLabelConfigsFromNames(values.labels, campaignLabels),
    };
  }, [campaign, values]);

  return (
    <CampaignContext.Provider
      value={{
        errors,
        campaign: isSendEditorImprovementsEnabled ? campaignValues : campaign,
        updateCampaign,
        isDirty: isSendEditorImprovementsEnabled ? isCampaignFormDirty || isDirty : isDirty,
        updateIsDirty: setIsDirty,
        campaignLabels,
        isReadOnly,
        setIsReadOnly,
        templateErrors,
        setTemplateErrors: filterTemplateErrors,
        placementName,
        setPlacementName,
        ...cloneMessaging,
      }}
    >
      {children}
    </CampaignContext.Provider>
  );
};

export default CampaignContext;
